import { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import Select from "react-select";
import ProposalList from "./ProposalList";
import ProposalForm from "./ProposalForm";
import AlertDialog from "./AlertDialog";
import {
  CheckIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";
import { useLocation } from "react-router-dom";
import SharePointFilesTab from "./OpportunitySharePointFilesTab";
const API = "https://erp-xe9u.onrender.com/api";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
    "&:focus": {
      outline: "none",
      boxShadow: "none",
      border: "none",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  }),
};

// Define valid options for status and estimationStatus based on schema
const STATUS_OPTIONS = [
  { value: "Undecided", label: "Undecided" },
  { value: "Lost", label: "Lost" },
  { value: "Won", label: "Won" },
];

const ESTIMATION_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

export default function OpportunityDetails({
  opportunity,
  proposals,
  setProposals,
  setOpportunities,
  users,
  accounts,
  contacts,
  companyProfile,
  userEmail,
  navigate,
}) {
  const [activeSubTab, setActiveSubTab] = useState("General");
  console.log(opportunity);
  const location = useLocation();
  const [isCreatingProposal, setIsCreatingProposal] = useState(false);
  const [isEditingProposal, setIsEditingProposal] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [noteSortOrder, setNoteSortOrder] = useState("desc");
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [newActivity, setNewActivity] = useState({
    title: "",
    dueDate: "",
    description: "",
    method: "email",
    assignedTo: null,
  });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isEditingActivity, setIsEditingActivity] = useState(false);
  const [editActivity, setEditActivity] = useState({
    title: "",
    dueDate: "",
    description: "",
    method: "email",
    status: "pending",
    assignedTo: null,
  });
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });
  const [isEditingGeneral, setIsEditingGeneral] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: "",
    status: "",
    estimationStatus: "",
    estimator: null,
    contact: null,
    account: null,
    bidders: [],
    source: "",
    estimateDueDate: "",
    estimatedRevenue: "",
    estimationCompletionDate: "",
    proposalSentDate: "",
    proposalSignedDate: "",
    drawingLinks: [],
    estimatedStartDate: "",
  });
  const [newBidder, setNewBidder] = useState(null);

  const tabs = [
    { name: "General", current: activeSubTab === "General" },
    { name: "Files", current: activeSubTab === "Files" },
    { name: "Proposals", current: activeSubTab === "Proposals" },
    { name: "Notes", current: activeSubTab === "Notes" },
    { name: "Activities", current: activeSubTab === "Activities" },
  ];

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "N/A";

  const formatDateForInput = (date) =>
    date ? new Date(date).toISOString().slice(0, 10) : "";

  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = jwtDecode(token);
        const currentUser = users.find((user) => user.email === decoded.email);
        return currentUser?._id || null;
      }
      return users.find((user) => user.email === userEmail)?._id || null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const userOptions = users.map((user) => ({
    value: user._id,
    label: user.displayName || user.email,
  }));

  const accountOptions = accounts.map((account) => ({
    value: account._id,
    label: account.companyName,
  }));

  const contactOptions = contacts.map((contact) => ({
    value: contact._id,
    label: `${contact.firstName} ${contact.lastName}`,
  }));

  const sourceOptions = [
    { value: "Other", label: "Other" },
    ...leadSources.map((source) => ({
      value: source,
      label: source,
    })),
  ];

  useEffect(() => {
    const fetchLeadSources = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/company-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLeadSources(
          response.data.plugins?.automatedLeads?.sources?.map(
            (s) => s.sourceName,
          ) || [],
        );
      } catch (err) {
        console.error("Error fetching company profile:", err);
      }
    };

    fetchLeadSources();

    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      setNewActivity((prev) => ({ ...prev, assignedTo: currentUserId }));
    }
    if (opportunity) {
      setEditFormData({
        title: opportunity.title || "",
        status:
          opportunity.status &&
          STATUS_OPTIONS.some((opt) => opt.value === opportunity.status)
            ? opportunity.status
            : "Undecided",
        estimationStatus:
          opportunity.estimationStatus &&
          ESTIMATION_STATUS_OPTIONS.some(
            (opt) => opt.value === opportunity.estimationStatus,
          )
            ? opportunity.estimationStatus
            : "pending",
        estimator: opportunity.estimator?._id || null,
        contact: opportunity.contact?._id || null,
        account: opportunity.account?._id || null,
        bidders: opportunity.bidders?.map((b) => b._id) || [],
        source: opportunity.source || "Other",
        estimateDueDate: formatDateForInput(opportunity.estimateDueDate),
        estimatedRevenue: opportunity.estimatedRevenue || "",
        estimationCompletionDate: formatDateForInput(
          opportunity.estimationCompletionDate,
        ),
        proposalSentDate: formatDateForInput(opportunity.proposalSentDate),
        proposalSignedDate: formatDateForInput(opportunity.proposalSignedDate),
        drawingLinks: opportunity.drawingLinks || [],
        estimatedStartDate: formatDateForInput(opportunity.estimatedStartDate),
      });
    }
  }, [opportunity, users]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const activityId = params.get("activityId");

    if (tab && tabs.some((t) => t.name === tab)) {
      setActiveSubTab(tab);
      if (tab === "Activities" && activityId && activities.length > 0) {
        const activity = activities.find((act) => act._id === activityId);
        if (activity) {
          setSelectedActivity(activity);
        }
      }
    }
  }, [location, activities]);

  const fetchProposals = async () => {
    if (!opportunity?._id) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Invalid opportunity ID",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/proposals?opportunity=${opportunity._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setProposals(response.data);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to fetch proposals: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const fetchTimeline = async () => {
    if (!opportunity?._id) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Invalid opportunity ID",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/opportunities/${opportunity._id}/timeline`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setTimelineEvents(response.data);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to fetch timeline: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const fetchActivities = async () => {
    if (!opportunity?._id) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Invalid opportunity ID",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/opportunities/${opportunity._id}/activities`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setActivities(response.data.activities);
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to fetch activities: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  useEffect(() => {
    if (opportunity?._id) {
      fetchProposals();
      fetchTimeline();
      fetchActivities();
    }
  }, [opportunity?._id]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!opportunity?._id) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Invalid opportunity ID",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }
    if (!noteContent.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Note content cannot be empty.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/opportunities/${opportunity._id}/notes`,
        { content: noteContent.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOpportunities((prev) =>
        prev.map((opp) =>
          opp._id === opportunity._id
            ? {
                ...opp,
                notes: [...(opp.notes || []), response.data.note],
              }
            : opp,
        ),
      );
      setNoteContent("");
      setShowNoteForm(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Note saved successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to save note: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!opportunity?._id) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Invalid opportunity ID",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    if (
      !newActivity.title.trim() ||
      !newActivity.dueDate ||
      !newActivity.description.trim() ||
      !newActivity.assignedTo
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Title, due date, description, and assigned user are required",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/opportunities/${opportunity._id}/activities`,
        newActivity,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setActivities((prev) => [...prev, response.data.activity]);
      setNewActivity({
        title: "",
        dueDate: "",
        description: "",
        method: "email",
        assignedTo: getCurrentUserId(),
      });
      setShowActivityForm(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Activity added successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to add activity: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleUpdateActivity = async (activityId, updates) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API}/opportunities/${opportunity._id}/activities/${activityId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setActivities((prev) =>
        prev.map((act) =>
          act._id === activityId ? response.data.activity : act,
        ),
      );
      setSelectedActivity(response.data.activity);
      setIsEditingActivity(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Activity updated successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to update activity: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleEditActivity = (activity) => {
    setIsEditingActivity(true);
    setEditActivity({
      title: activity.title,
      dueDate: new Date(activity.dueDate).toISOString().slice(0, 16),
      description: activity.description,
      method: activity.method,
      status: activity.status,
      assignedTo: activity.assignedTo?._id || activity.createdBy?._id,
    });
  };

  const handleSubmitEditActivity = async (e) => {
    e.preventDefault();
    if (
      !editActivity.title.trim() ||
      !editActivity.dueDate ||
      !editActivity.description.trim() ||
      !editActivity.assignedTo
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Title, due date, description, and assigned user are required",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    await handleUpdateActivity(selectedActivity._id, editActivity);
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/opportunities/${opportunity._id}/activities/${activityId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setActivities((prev) => prev.filter((act) => act._id !== activityId));
      setSelectedActivity(null);
      setIsEditingActivity(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Activity deleted successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to delete activity: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleUpdateOpportunity = async (e) => {
    e.preventDefault();
    if (!opportunity?._id) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Invalid opportunity ID",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    if (!editFormData.title.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Title is required",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    if (editFormData.status === "Won" && !editFormData.proposalSignedDate) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Cannot set status to Won. Please use the 'Awarded' action to set the status and proposal signed date.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    if (
      opportunity?.status === "Won" &&
      opportunity?.proposalSignedDate &&
      editFormData.status === "Undecided"
    ) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Cannot change status to Undecided from Won. Please select Lost to clear the proposal signed date or keep the status as Won.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const { proposalSignedDate, ...payload } = editFormData;
      const response = await axios.put(
        `${API}/opportunities/${opportunity._id}`,
        {
          ...payload,
          drawingLinks: editFormData.drawingLinks.filter((link) => link.trim()),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOpportunities((prev) =>
        prev.map((opp) => (opp._id === opportunity._id ? response.data : opp)),
      );
      setIsEditingGeneral(false);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Opportunity updated successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to update opportunity: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleAddDrawingLink = () => {
    setEditFormData({
      ...editFormData,
      drawingLinks: [...editFormData.drawingLinks, ""],
    });
  };

  const handleRemoveDrawingLink = (index) => {
    setEditFormData({
      ...editFormData,
      drawingLinks: editFormData.drawingLinks.filter((_, i) => i !== index),
    });
  };

  const handleDrawingLinkChange = (index, value) => {
    const updatedLinks = [...editFormData.drawingLinks];
    updatedLinks[index] = value;
    setEditFormData({ ...editFormData, drawingLinks: updatedLinks });
  };

  const handleAddBidder = async () => {
    if (!newBidder) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select an account to add as a bidder.",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/opportunities/${opportunity._id}/bidders`,
        { accountId: newBidder },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOpportunities((prev) =>
        prev.map((opp) =>
          opp._id === opportunity._id ? response.data.opportunity : opp,
        ),
      );
      setNewBidder(null);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Bidder added successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to add bidder: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleRemoveBidder = async (accountId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `${API}/opportunities/${opportunity._id}/bidders/${accountId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOpportunities((prev) =>
        prev.map((opp) =>
          opp._id === opportunity._id ? response.data.opportunity : opp,
        ),
      );
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Bidder removed successfully!",
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
      fetchTimeline();
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to remove bidder: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleSourceChange = (e) => {
    setEditFormData((prev) => ({
      ...prev,
      source: e.target.value,
    }));
  };

  const toggleNoteSortOrder = () => {
    setNoteSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const sortedNotes = opportunity?.notes
    ? [...opportunity.notes].sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return noteSortOrder === "desc" ? dateB - dateA : dateA - dateB;
      })
    : [];

  const pendingActivities = activities
    .filter((act) => act.status === "pending")
    .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));

  const getEventStatus = (event, index) => {
    const now = new Date();
    const eventDate = new Date(event.timestamp);
    if (eventDate <= now) {
      return index === 0 ? "current" : "complete";
    }
    return "upcoming";
  };

  if (!opportunity) {
    return (
      <div className="text-red-600 text-center py-4">Opportunity not found</div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 py-6">
      <div className="bg-gray-200 shadow-xl rounded-xl p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-bold text-white bg-gray-700 rounded-md px-4 py-2">
            {opportunity.title}
          </h1>
          <button
            onClick={() => navigate("/Opportunities")}
            className="bg-gray-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-gray-500"
          >
            Back to Opportunities
          </button>
        </div>

        <div className="mb-6">
          <div className="grid grid-cols-1 sm:hidden">
            <select
              defaultValue={tabs.find((tab) => tab.current).name}
              onChange={(e) => setActiveSubTab(e.target.value)}
              aria-label="Select a tab"
              className="col-start-1 font-semibold row-start-1 w-full appearance-none rounded-md bg-gray-800 py-2 pr-8 pl-3 text-base text-gray-200 outline-1 -outline-offset-1 outline-gray-600 focus:outline-2 focus:-outline-offset-2 focus:outline-orange-400 focus:font-semibold"
            >
              {tabs.map((tab) => (
                <option key={tab.name} value={tab.name}>
                  {tab.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              aria-hidden="true"
              className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-400"
            />
          </div>
          <div className="hidden sm:block">
            <nav
              aria-label="Tabs"
              className="isolate flex divide-x divide-gray-600 rounded-lg shadow-sm"
            >
              {tabs.map((tab, tabIdx) => (
                <button
                  key={tab.name}
                  onClick={() => setActiveSubTab(tab.name)}
                  aria-current={tab.current ? "page" : undefined}
                  className={classNames(
                    tab.current
                      ? "text-gray-200"
                      : "text-gray-400 hover:text-gray-200",
                    tabIdx === 0 ? "rounded-l-lg" : "",
                    tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                    "group relative min-w-0 flex-1 overflow-hidden bg-gray-700 px-4 py-4 text-center text-sm hover:bg-gray-700 focus:z-10 font-semibold",
                  )}
                >
                  <span>{tab.name}</span>
                  <span
                    aria-hidden="true"
                    className={classNames(
                      tab.current ? "bg-orange-400" : "bg-transparent",
                      "absolute inset-x-0 bottom-0 h-0.5",
                    )}
                  />
                </button>
              ))}
            </nav>
          </div>
        </div>

        {activeSubTab === "General" && (
          <div>
            <div className="flex gap-6">
              <div className="bg-gray-700 p-6 rounded-lg shadow-lg flex-1">
                <div className="flex justify-between items-center">
                  {!isEditingGeneral ? (
                    <>
                      <div></div>
                      <button
                        onClick={() => setIsEditingGeneral(true)}
                        className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                      >
                        Edit
                      </button>
                    </>
                  ) : (
                    <div className="flex space-x-4">
                      <button
                        onClick={() => setIsEditingGeneral(false)}
                        className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateOpportunity}
                        className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                      >
                        Save
                      </button>
                    </div>
                  )}
                </div>
                {isEditingGeneral ? (
                  <form onSubmit={handleUpdateOpportunity}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Title
                        </label>
                        <input
                          type="text"
                          value={editFormData.title}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              title: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Status
                        </label>
                        <Select
                          options={STATUS_OPTIONS}
                          value={STATUS_OPTIONS.find(
                            (option) => option.value === editFormData.status,
                          )}
                          onChange={(selected) =>
                            setEditFormData({
                              ...editFormData,
                              status: selected ? selected.value : "",
                            })
                          }
                          className="mt-1"
                          styles={customSelectStyles}
                          isClearable
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Estimation Status
                        </label>
                        <Select
                          options={ESTIMATION_STATUS_OPTIONS}
                          value={ESTIMATION_STATUS_OPTIONS.find(
                            (option) =>
                              option.value === editFormData.estimationStatus,
                          )}
                          onChange={(selected) =>
                            setEditFormData({
                              ...editFormData,
                              estimationStatus: selected ? selected.value : "",
                            })
                          }
                          className="mt-1"
                          styles={customSelectStyles}
                          isClearable
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Estimator
                        </label>
                        <Select
                          options={userOptions}
                          value={userOptions.find(
                            (option) => option.value === editFormData.estimator,
                          )}
                          onChange={(selected) =>
                            setEditFormData({
                              ...editFormData,
                              estimator: selected ? selected.value : null,
                            })
                          }
                          className="mt-1"
                          styles={customSelectStyles}
                          isClearable
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Contact
                        </label>
                        <Select
                          options={contactOptions}
                          value={contactOptions.find(
                            (option) => option.value === editFormData.contact,
                          )}
                          onChange={(selected) =>
                            setEditFormData({
                              ...editFormData,
                              contact: selected ? selected.value : null,
                            })
                          }
                          className="mt-1"
                          styles={customSelectStyles}
                          isClearable
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Account
                        </label>
                        <Select
                          options={accountOptions}
                          value={accountOptions.find(
                            (option) => option.value === editFormData.account,
                          )}
                          onChange={(selected) =>
                            setEditFormData({
                              ...editFormData,
                              account: selected ? selected.value : null,
                            })
                          }
                          className="mt-1"
                          styles={customSelectStyles}
                          isClearable
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Bidders
                        </label>
                        <Select
                          options={accountOptions}
                          value={accountOptions.filter((option) =>
                            editFormData.bidders.includes(option.value),
                          )}
                          onChange={(selected) =>
                            setEditFormData({
                              ...editFormData,
                              bidders: selected
                                ? selected.map((opt) => opt.value)
                                : [],
                            })
                          }
                          className="mt-1"
                          styles={customSelectStyles}
                          isMulti
                          placeholder="Select bidders..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Source
                        </label>
                        <select
                          value={editFormData.source}
                          onChange={handleSourceChange}
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        >
                          {sourceOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Estimate Due Date
                        </label>
                        <input
                          type="date"
                          value={editFormData.estimateDueDate}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              estimateDueDate: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Estimated Revenue
                        </label>
                        <input
                          type="number"
                          value={editFormData.estimatedRevenue}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              estimatedRevenue: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Estimation Completion Date
                        </label>
                        <input
                          type="date"
                          value={editFormData.estimationCompletionDate}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              estimationCompletionDate: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Proposal Sent Date
                        </label>
                        <input
                          type="date"
                          value={editFormData.proposalSentDate}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              proposalSentDate: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Proposal Signed Date
                        </label>
                        <input
                          type="date"
                          value={editFormData.proposalSignedDate}
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm cursor-not-allowed"
                          disabled
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          This field is set automatically when the opportunity
                          is awarded.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Estimated Start Date
                        </label>
                        <input
                          type="date"
                          value={editFormData.estimatedStartDate}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              estimatedStartDate: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-400">
                          Drawing Links
                        </label>
                        {editFormData.drawingLinks.map((link, index) => (
                          <div key={index} className="flex items-center mt-1">
                            <input
                              type="url"
                              value={link}
                              onChange={(e) =>
                                handleDrawingLinkChange(index, e.target.value)
                              }
                              className="block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                              placeholder="Enter drawing link"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveDrawingLink(index)}
                              className="ml-2 text-red-400 hover:text-red-300"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={handleAddDrawingLink}
                          className="mt-2 inline-flex items-center px-3 py-1.5 rounded-md bg-gray-700 text-sm font-medium text-orange-400 hover:bg-gray-600"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Drawing Link
                        </button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Title
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        <span className="inline-flex items-center rounded-full bg-gray-200 px-4 py-0.5 text-sm font-medium text-black hover:bg-gray-300">
                          {opportunity.title}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Status
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        <span
                          className={classNames(
                            opportunity.status === "Won"
                              ? "bg-green-100 text-green-700"
                              : opportunity.status === "Lost"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700",
                            "inline-flex items-center gap-x-1.5 rounded-full px-4 py-0.5 text-sm font-medium",
                          )}
                        >
                          <svg
                            viewBox="0 0 6 6"
                            aria-hidden="true"
                            className={classNames(
                              opportunity.status === "Won"
                                ? "fill-green-500"
                                : opportunity.status === "Lost"
                                  ? "fill-red-500"
                                  : "fill-yellow-500",
                              "size-1.5",
                            )}
                          >
                            <circle r={3} cx={3} cy={3} />
                          </svg>
                          {opportunity.status}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Estimation Status
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        <span
                          className={classNames(
                            opportunity.estimationStatus === "completed"
                              ? "bg-green-100 text-green-700"
                              : opportunity.estimationStatus === "in_progress"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-gray-100 text-gray-600",
                            "inline-flex items-center gap-x-1.5 rounded-full px-4 py-0.5 text-sm font-medium",
                          )}
                        >
                          <svg
                            viewBox="0 0 6 6"
                            aria-hidden="true"
                            className={classNames(
                              opportunity.estimationStatus === "completed"
                                ? "fill-green-500"
                                : opportunity.estimationStatus === "in_progress"
                                  ? "fill-yellow-500"
                                  : "fill-gray-400",
                              "size-1.5",
                            )}
                          >
                            <circle r={3} cx={3} cy={3} />
                          </svg>
                          {ESTIMATION_STATUS_OPTIONS.find(
                            (opt) => opt.value === opportunity.estimationStatus,
                          )?.label || opportunity.estimationStatus}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Estimator
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {opportunity.estimator?.displayName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Contact
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {opportunity.contact
                          ? `${opportunity.contact.firstName} ${opportunity.contact.lastName}`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Primary Account
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {opportunity.account?.companyName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        List of Bidders
                      </label>
                      <div className="mt-1 text-sm text-gray-300">
                        {opportunity.bidders &&
                        opportunity.bidders.length > 0 ? (
                          <ul className="list-disc pl-5">
                            {opportunity.bidders.map((bidder) => (
                              <li
                                key={bidder._id}
                                className="flex items-center"
                              >
                                {bidder.companyName}
                                <button
                                  onClick={() => handleRemoveBidder(bidder._id)}
                                  className="ml-2 text-red-400 hover:text-red-300"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>No bidders</p>
                        )}
                      </div>
                      <div className="mt-2 flex items-center">
                        <Select
                          options={accountOptions}
                          value={accountOptions.find(
                            (option) => option.value === newBidder,
                          )}
                          onChange={(selected) =>
                            setNewBidder(selected ? selected.value : null)
                          }
                          placeholder="Add bidder..."
                          className="mt-1 flex-1"
                          styles={customSelectStyles}
                          isClearable
                        />
                        <button
                          type="button"
                          onClick={handleAddBidder}
                          className="ml-2 bg-orange-400 text-white px-3 py-1.5 rounded-md hover:bg-orange-500"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Source
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {opportunity.source || "Other"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Estimate Due Date
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {formatDate(opportunity.estimateDueDate)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Estimated Revenue
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        $
                        {opportunity.estimatedRevenue.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Estimation Completion Date
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {formatDate(opportunity.estimationCompletionDate)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Proposal Sent Date
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {formatDate(opportunity.proposalSentDate)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Proposal Signed Date
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {formatDate(opportunity.proposalSignedDate)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Estimated Start Date
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {formatDate(opportunity.estimatedStartDate)}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-400">
                        Drawing Links
                      </label>
                      {opportunity.drawingLinks &&
                      opportunity.drawingLinks.length > 0 ? (
                        opportunity.drawingLinks.map((link, idx) => (
                          <div key={idx} className="text-sm text-gray-300">
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-orange-400 hover:underline"
                            >
                              {link}
                            </a>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-300">
                          No drawing links
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {pendingActivities.length > 0 && (
                  <div className="mt-6">
                    <h2 className="text-lg font-semibold text-orange-400 mb-2">
                      Next Tasks
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {pendingActivities.map((activity) => {
                        const dueDate = new Date(activity.dueDate);
                        const now = new Date();
                        const isPastDue =
                          Date.UTC(
                            dueDate.getFullYear(),
                            dueDate.getMonth(),
                            dueDate.getDate(),
                          ) <
                          Date.UTC(
                            now.getFullYear(),
                            now.getMonth(),
                            now.getDate(),
                          );
                        return (
                          <button
                            key={activity._id}
                            onClick={() => {
                              setActiveSubTab("Activities");
                              setSelectedActivity(activity);
                            }}
                            className={classNames(
                              "inline-flex flex-col items-start px-4 py-2 rounded-lg text-sm font-medium",
                              isPastDue
                                ? "bg-red-100 text-red-700 hover:bg-red-200"
                                : "bg-orange-100 text-orange-700 hover:bg-orange-200",
                            )}
                          >
                            <span>{activity.title}</span>
                            <span className="text-xs mt-1">
                              Due: {formatDate(dueDate)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-gray-700 p-6 rounded-lg shadow-lg w-1/3">
                <nav aria-label="Progress">
                  <ol role="list" className="overflow-hidden">
                    {timelineEvents.length === 0 ? (
                      <p className="text-sm text-gray-300">
                        No timeline events available.
                      </p>
                    ) : (
                      timelineEvents.map((event, eventIdx) => {
                        const status = getEventStatus(event, eventIdx);
                        return (
                          <li
                            key={event.id}
                            className={classNames(
                              eventIdx !== timelineEvents.length - 1
                                ? "pb-10"
                                : "",
                              "relative",
                            )}
                          >
                            {status === "complete" ? (
                              <>
                                {eventIdx !== timelineEvents.length - 1 ? (
                                  <div
                                    aria-hidden="true"
                                    className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-orange-400"
                                  />
                                ) : null}
                                <div className="group relative flex items-start">
                                  <span className="flex h-9 items-center">
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full bg-orange-400 group-hover:bg-orange-500">
                                      <CheckIcon
                                        aria-hidden="true"
                                        className="size-5 text-white"
                                      />
                                    </span>
                                  </span>
                                  <span className="ml-4 flex min-w-0 flex-col">
                                    <span className="text-sm font-medium text-gray-200">
                                      {event.action.charAt(0).toUpperCase() +
                                        event.action.slice(1)}{" "}
                                      {event.entityType}
                                    </span>
                                    <span className="text-sm text-gray-300">
                                      {event.description}
                                      <br />
                                      By {event.changedBy} on{" "}
                                      {formatDate(event.timestamp)}
                                    </span>
                                  </span>
                                </div>
                              </>
                            ) : status === "current" ? (
                              <>
                                {eventIdx !== timelineEvents.length - 1 ? (
                                  <div
                                    aria-hidden="true"
                                    className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-600"
                                  />
                                ) : null}
                                <div
                                  aria-current="step"
                                  className="group relative flex items-start"
                                >
                                  <span
                                    aria-hidden="true"
                                    className="flex h-9 items-center"
                                  >
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-orange-400 bg-gray-800">
                                      <span className="size-2.5 rounded-full bg-orange-400" />
                                    </span>
                                  </span>
                                  <span className="ml-4 flex min-w-0 flex-col">
                                    <span className="text-sm font-medium text-orange-400">
                                      {event.action.charAt(0).toUpperCase() +
                                        event.action.slice(1)}{" "}
                                      {event.entityType}
                                    </span>
                                    <span className="text-sm text-gray-300">
                                      {event.description}
                                      <br />
                                      By {event.changedBy} on{" "}
                                      {formatDate(event.timestamp)}
                                    </span>
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                {eventIdx !== timelineEvents.length - 1 ? (
                                  <div
                                    aria-hidden="true"
                                    className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-600"
                                  />
                                ) : null}
                                <div className="group relative flex items-start">
                                  <span
                                    aria-hidden="true"
                                    className="flex h-9 items-center"
                                  >
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-gray-600 bg-gray-800 group-hover:border-gray-500">
                                      <span className="size-2.5 rounded-full bg-transparent group-hover:bg-gray-600" />
                                    </span>
                                  </span>
                                  <span className="ml-4 flex min-w-0 flex-col">
                                    <span className="text-sm font-medium text-gray-400">
                                      {event.action.charAt(0).toUpperCase() +
                                        event.action.slice(1)}{" "}
                                      {event.entityType}
                                    </span>
                                    <span className="text-sm text-gray-300">
                                      {event.description}
                                      <br />
                                      By {event.changedBy} on{" "}
                                      {formatDate(event.timestamp)}
                                    </span>
                                  </span>
                                </div>
                              </>
                            )}
                          </li>
                        );
                      })
                    )}
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "Files" && (
          <SharePointFilesTab
            entityId={opportunity._id}
            entityName={opportunity.title}
            entityType="Opportunity"
            sharePointFolderName={opportunity.sharePointFolderName}
            setEntity={(updatedOpportunity) =>
              setOpportunities((prev) =>
                prev.map((opp) =>
                  opp._id === opportunity._id ? updatedOpportunity : opp,
                ),
              )
            }
          />
        )}

        {activeSubTab === "Proposals" && (
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-orange-400"></h2>
              <button
                onClick={() => setIsCreatingProposal(true)}
                className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
              >
                Create Proposal
              </button>
            </div>
            {(isCreatingProposal || isEditingProposal) && (
              <ProposalForm
                opportunityId={opportunity._id}
                setProposals={setProposals}
                setOpportunities={setOpportunities}
                setIsCreatingProposal={setIsCreatingProposal}
                setIsEditingProposal={setIsEditingProposal}
                proposal={
                  isEditingProposal
                    ? proposals.find((p) => p._id === isEditingProposal)
                    : null
                }
                fetchProposals={fetchProposals}
              />
            )}
            <ProposalList
              proposals={proposals.filter(
                (prop) =>
                  (prop.opportunity?._id || prop.opportunity) ===
                  opportunity._id,
              )}
              setProposals={setProposals}
              setOpportunities={setOpportunities}
              opportunityId={opportunity._id}
              companyProfile={companyProfile}
              userEmail={userEmail}
              accounts={accounts}
              contacts={contacts}
              opportunities={[opportunity]}
              setIsEditingProposal={setIsEditingProposal}
              fetchProposals={fetchProposals}
            />
          </div>
        )}

        {activeSubTab === "Notes" && (
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-orange-400">Notes</h2>
              <div className="flex space-x-4">
                <button
                  onClick={toggleNoteSortOrder}
                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                >
                  Sort{" "}
                  {noteSortOrder === "desc" ? "Oldest First" : "Newest First"}
                </button>
                <button
                  onClick={() => setShowNoteForm(true)}
                  className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                >
                  Add Note
                </button>
              </div>
            </div>
            {showNoteForm && (
              <form onSubmit={handleAddNote} className="mb-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Add a Note
                  </label>
                  <textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    rows={4}
                    placeholder="Enter your note here..."
                  />
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNoteForm(false);
                      setNoteContent("");
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                  >
                    Save Note
                  </button>
                </div>
              </form>
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">Notes</h3>
              {sortedNotes.length === 0 ? (
                <p className="text-gray-300">No notes available.</p>
              ) : (
                <ul className="divide-y divide-gray-600">
                  {sortedNotes.map((note, index) => (
                    <li key={index} className="py-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm text-gray-200">
                            {note.content}
                          </p>
                          <p className="text-xs text-gray-300 mt-1">
                            By {note.createdBy?.displayName || "Unknown"} on{" "}
                            {formatDate(note.createdAt)}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {activeSubTab === "Activities" && (
          <div className="bg-gray-700 p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-orange-400">
                Activities
              </h2>
              <button
                onClick={() => {
                  setShowActivityForm(true);
                  setIsEditingActivity(false);
                  setNewActivity({
                    title: "",
                    dueDate: "",
                    description: "",
                    method: "email",
                    assignedTo: getCurrentUserId(),
                  });
                }}
                className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
              >
                Add Activity
              </button>
            </div>

            {showActivityForm && (
              <form onSubmit={handleAddActivity} className="mb-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Title
                    </label>
                    <input
                      type="text"
                      value={newActivity.title}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          title: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      placeholder="Enter activity title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Due Date
                    </label>
                    <input
                      type="datetime-local"
                      value={newActivity.dueDate}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          dueDate: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-400">
                      Description
                    </label>
                    <textarea
                      value={newActivity.description}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          description: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      rows={4}
                      placeholder="Enter activity description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Method
                    </label>
                    <select
                      value={newActivity.method}
                      onChange={(e) =>
                        setNewActivity({
                          ...newActivity,
                          method: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    >
                      <option value="email">Email</option>
                      <option value="call">Call</option>
                      <option value="in-person">In-Person</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">
                      Assigned To
                    </label>
                    <Select
                      options={userOptions}
                      value={userOptions.find(
                        (option) => option.value === newActivity.assignedTo,
                      )}
                      onChange={(selected) =>
                        setNewActivity({
                          ...newActivity,
                          assignedTo: selected ? selected.value : null,
                        })
                      }
                      placeholder="Select user"
                      className="mt-1"
                      styles={customSelectStyles}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-4 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowActivityForm(false);
                      setNewActivity({
                        title: "",
                        dueDate: "",
                        description: "",
                        method: "email",
                        assignedTo: getCurrentUserId(),
                      });
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                  >
                    Save Activity
                  </button>
                </div>
              </form>
            )}

            {selectedActivity && (
              <div className="mb-6 p-4 bg-gray-700 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-orange-400">
                    {selectedActivity.title}
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditActivity(selectedActivity)}
                      className="text-orange-400 hover:text-orange-300"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setSelectedActivity(null)}
                      className="text-gray-400 hover:text-gray-200"
                    >
                      Close
                    </button>
                  </div>
                </div>

                {isEditingActivity ? (
                  <form onSubmit={handleSubmitEditActivity} className="mb-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Title
                        </label>
                        <input
                          type="text"
                          value={editActivity.title}
                          onChange={(e) =>
                            setEditActivity({
                              ...editActivity,
                              title: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          placeholder="Enter activity title"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Due Date
                        </label>
                        <input
                          type="datetime-local"
                          value={editActivity.dueDate}
                          onChange={(e) =>
                            setEditActivity({
                              ...editActivity,
                              dueDate: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-400">
                          Description
                        </label>
                        <textarea
                          value={editActivity.description}
                          onChange={(e) =>
                            setEditActivity({
                              ...editActivity,
                              description: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                          rows={4}
                          placeholder="Enter activity description"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Method
                        </label>
                        <select
                          value={editActivity.method}
                          onChange={(e) =>
                            setEditActivity({
                              ...editActivity,
                              method: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        >
                          <option value="email">Email</option>
                          <option value="call">Call</option>
                          <option value="in-person">In-Person</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Status
                        </label>
                        <select
                          value={editActivity.status}
                          onChange={(e) =>
                            setEditActivity({
                              ...editActivity,
                              status: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                        >
                          <option value="pending">Pending</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400">
                          Assigned To
                        </label>
                        <Select
                          options={userOptions}
                          value={userOptions.find(
                            (option) =>
                              option.value === editActivity.assignedTo,
                          )}
                          onChange={(selected) =>
                            setEditActivity({
                              ...editActivity,
                              assignedTo: selected ? selected.value : null,
                            })
                          }
                          placeholder="Select user"
                          className="mt-1"
                          styles={customSelectStyles}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-4 mt-4">
                      <button
                        type="button"
                        onClick={() => setIsEditingActivity(false)}
                        className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Due Date
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {formatDate(selectedActivity.dueDate)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Method
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {selectedActivity.method}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Status
                      </label>
                      <select
                        value={selectedActivity.status}
                        onChange={(e) =>
                          handleUpdateActivity(selectedActivity._id, {
                            status: e.target.value,
                          })
                        }
                        className="mt-1 block w-full rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Description
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {selectedActivity.description}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Created By
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {selectedActivity.createdBy?.displayName || "Unknown"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Assigned To
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {selectedActivity.assignedTo?.displayName ||
                          selectedActivity.createdBy?.displayName ||
                          "Unknown"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Created On
                      </label>
                      <p className="mt-1 text-sm text-gray-300">
                        {formatDate(selectedActivity.createdOn)}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex justify-end space-x-4 mt-4">
                  <button
                    onClick={() =>
                      setAlert({
                        open: true,
                        type: "warning",
                        title: "Confirm Delete",
                        message:
                          "Are you sure                        you want to delete this activity?",
                        onConfirm: () => {
                          handleDeleteActivity(selectedActivity._id);
                          setAlert({ ...alert, open: false });
                        },
                        showCancel: true,
                      })
                    }
                    className="bg-red-100 text-red-700 px-4 py-2 rounded-md hover:bg-red-200"
                  >
                    <TrashIcon className="h-5 w-5 inline mr-1" />
                    Delete Activity
                  </button>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">
                All Activities
              </h3>
              {activities.length === 0 ? (
                <p className="text-gray-300">No activities available.</p>
              ) : (
                <ul className="divide-y divide-gray-600">
                  {activities
                    .sort(
                      (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
                    )
                    .map((activity) => (
                      <li
                        key={activity._id}
                        className="py-4 cursor-pointer hover:bg-gray-600"
                        onClick={() => {
                          setSelectedActivity(activity);
                          setIsEditingActivity(false);
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-200">
                              {activity.title}
                            </p>
                            <p className="text-sm text-gray-300">
                              {activity.description}
                            </p>
                            <p className="text-xs text-gray-300 mt-1">
                              {activity.method} | {activity.status} | Due:{" "}
                              {formatDate(activity.dueDate)} | By{" "}
                              {activity.createdBy?.displayName || "Unknown"} |
                              Assigned To:{" "}
                              {activity.assignedTo?.displayName ||
                                activity.createdBy?.displayName ||
                                "Unknown"}
                            </p>
                          </div>
                          <span
                            className={classNames(
                              activity.status === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700",
                              "inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
                            )}
                          >
                            <svg
                              viewBox="0 0 6 6"
                              aria-hidden="true"
                              className={classNames(
                                activity.status === "pending"
                                  ? "fill-yellow-500"
                                  : "fill-green-500",
                                "size-1.5",
                              )}
                            >
                              <circle r={3} cx={3} cy={3} />
                            </svg>
                            {activity.status.charAt(0).toUpperCase() +
                              activity.status.slice(1)}
                          </span>
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert({ ...alert, open: false })}
          onConfirm={alert.onConfirm}
          showCancel={alert.showCancel}
        />
      </div>
    </div>
  );
}
